import moment from 'moment/moment';
import {getConfig} from './configUtil';

/**
 * Calculates the end date of a given episode within a sorted list of episodes.
 *
 * The function determines the end date of an episode based on its position in the episodes list:
 * - If the position refers to the last episode, the end date will be the provided `admissionEndDate` `-` if it's not available.
 * - If the position refers to any earlier episode, the start date of the next episode is used as the end date.
 *
 * **Important:** The `episodes` array must be sorted in descending order by `startDate` to ensure correct calculations.
 *
 * @param {Array<Object>} episodes - An array of episode objects, sorted by `startDate` in descending order.
 * @param {number} position - The position of the current episode (1-based index).
 * @param {string} [admissionEndDate] - The end date of the entire admission, used for the last episode.
 *
 * @returns {string} The formatted end date of the episode, or a dash (`-`) if no valid end date is available.
 *
 * @example
 * // Example with sorted episodes and an admission end date
 * const endDate = calculateEndDateOfEpisode(episodes, 1, '2023-09-01T10:00:00Z');
 *
 * // Example for the last episode without an admission end date
 * const endDate = calculateEndDateOfEpisode(episodes, episodes.length);
 */
export const calculateEndDateOfEpisode = (episodes, position, admissionEndDate) => {
    const dateFormat = getConfig('defaultDateTimeFormat');

    let endDate = null;
    if (position === episodes.length) {
        endDate = admissionEndDate ? moment(admissionEndDate) : '-';
    } else if (position < episodes.length) {
        const nextEpisode = episodes[episodes.length - position - 1];
        endDate = nextEpisode?.startDate ? moment(nextEpisode?.startDate) : '-';
    }
    return moment.isMoment(endDate) ? moment(endDate)?.format(dateFormat) : endDate;
};

/**
 * Determines the type of episode transfer based on changes between the current and previous episode.
 *
 * The function compares the current episode with the previous one (based on the position) to determine
 * if a change in clinician, specialty, or location occurred. Depending on the number of changes,
 * it returns a specific transfer type (e.g., "Location Transfer" or "Clinician / Speciality Transfer").
 *
 * If the episode is the first one (`position === 1`), it returns "Admission".
 *
 * **Important:** The `episodes` array must be sorted in descending order by `startDate` for correct behavior.
 *
 * @param {Object} currentEpisode - The current episode object.
 * @param {Array<Object>} episodes - An array of episodes, sorted by `startDate` in descending order.
 * @param {number} position - The position of the current episode (1-based index).
 *
 * @returns {string} The type of transfer based on the comparison with the previous episode,
 * or "Admission" if it's the first episode.
 *
 * @example
 * // Example with sorted episodes, returns a transfer type based on the differences
 * const transferType = getEpisodeType(currentEpisode, episodes, 2);
 *
 * // Example for the first episode, returns 'Admission'
 * const transferType = getEpisodeType(currentEpisode, episodes, 1);
 */
export const getEpisodeType = (currentEpisode, episodes, position) => {
    if (position - 1 === 0) {
        return 'Admission';
    }
    const prevEpisode = episodes[episodes.length - position + 1];

    const { consultant_id: prevConsultantId, specialty: prevSpecialty, ward_id: prevWardId } = prevEpisode || {};
    const { consultant_id: currentConsultantId, specialty: currentSpecialty, ward_id: currentWardId } = currentEpisode || {};

    const clinicianChange = prevConsultantId !== currentConsultantId;
    const specChange = prevSpecialty?.code !== currentSpecialty?.code;
    const locationChange = prevWardId !== currentWardId;

    const changes = [clinicianChange, specChange, locationChange];
    const isMulti = changes.filter(Boolean).length >= 2;

    const parts = [
        locationChange && 'Location',
        specChange && 'Speciality',
        clinicianChange && 'Clinician',
    ].filter(Boolean);

    const transferType = parts.join(' / ') + ' Transfer';

    return isMulti ? transferType : `${parts.join('')} Transfer`;
};